<script setup>
import BaseModal from "@/components/global-themed/BaseModal.vue";
import {computed, ref} from "vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import ErrorMessages from "@/components/global/ErrorMessages.vue";
import {useMutation} from "@tanstack/vue-query";
import axios from "axios";
import DefaultTextarea from "@/components/global-themed/inputs/DefaultTextarea.vue";
import ModalContentWrapper from "@/components/global-themed/ui/modals/ModalContentWrapper.vue";
import ModalHeading from "@/components/global-themed/ui/modals/ModalHeading.vue";
import ModalSubText from "@/components/global-themed/ui/modals/ModalSubText.vue";
import ModalHeader from "@/components/global-themed/ui/modals/ModalHeader.vue";
import {errorToast, successToast} from "@/composables/useToasts";

defineProps({
  showModal: {
    type: Boolean,
    required: true,
  }
});

const emit = defineEmits(['submitted']);

const feedback = ref('');
const showFeedbackTextarea = ref(false);

const errors = computed(() => error.value?.response?.data?.errors);

const {mutate: handleSubmit, error, isPending} = useMutation({
  mutationFn: async () => await axios.post('/ajax/v2/ai-onboarding/pages/feedback', {
    feedback: feedback.value,
  }),
  onSuccess: () => {
    successToast('Thanks for submitting your feedback, we will be in touch within 1 working day')
    emit('submitted');
  },
  onError: ({response}) => {
    errorToast(response.data.message ?? 'Something went wrong');
  },
});

</script>

<template>
  <Teleport to=".tailwind">
    <BaseModal
      v-if="showModal"
      modal-class="tw-max-w-[800px]"
      @close="() => emit('close')"
    >
      <ModalContentWrapper class="tw-gap-6">
        <ModalHeader>
          <ModalHeading>Happy with your new SEO strategy? 🚀</ModalHeading>
          <ModalSubText>
            I’ve built your initial SEO strategy using your ideal customers, key products/services, and target locations. This plan is designed to give your website a fast boost in traffic and leads.
          </ModalSubText>
          <ModalSubText>
            If everything looks good, you’re ready to move forward. If not, please share your feedback below. One of my managers will review your comments and fine-tune the strategy in the next working day, and get in touch afterwards.
          </ModalSubText>
        </ModalHeader>

        <form
          action=""
          @submit.prevent="handleSubmit"
        >
          <div
            v-if="showFeedbackTextarea"
            class="tw-space-4"
          >
            <DefaultTextarea
              v-model="feedback"
              placeholder="Enter feedback here..."
            />
            <ErrorMessages :errors="errors?.feedback ?? []" />

            <div class="tw-mt-4 tw-flex tw-justify-between">
              <DefaultButton
                variant="primary-inverted"
                :disabled="isPending"
                :loading="isPending"
                @click="showFeedbackTextarea = false"
              >
                Back
              </DefaultButton>

              <DefaultButton
                type="submit"
                :disabled="isPending || !feedback"
                :loading="isPending"
              >
                Submit
              </DefaultButton>
            </div>
          </div>

          <div
            v-else
            class="tw-flex tw-mt-6 tw-gap-4 tw-justify-between"
          >
            <DefaultButton
              variant="primary-inverted"
              :disabled="isPending"
              :loading="isPending"
              @click="showFeedbackTextarea = true"
            >
              No, I have a few tweaks
            </DefaultButton>

            <DefaultButton @click="emit('submitted')">
              Yes, I’m happy - let’s get going!
            </DefaultButton>
          </div>
        </form>
      </ModalContentWrapper>
    </BaseModal>
  </Teleport>
</template>
